/**
 * /settings/subnetwork-favicon — per-subnetwork favicon upload (FS-678).
 *
 * Declares a TanStack Router route and renders `SubnetworkFaviconPage`. The
 * component is imported by the root-owned `router.tsx` via module
 * augmentation — agents never touch `router.tsx` themselves; the root
 * wire-up cycle picks this route up after the prod merge.
 *
 * Search params reserved for `?id=<subnetwork_id>` so operators can
 * deep-link directly to a sub-network's favicon settings.
 */
import { Link, createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { SubnetworkFaviconPage } from "@/features/subnetwork-favicon/SubnetworkFaviconPage";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

interface SubnetworkFaviconSearch {
	id?: string;
}

export const settingsSubnetworkFaviconRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/settings/subnetwork-favicon",
	validateSearch: (raw: Record<string, unknown>): SubnetworkFaviconSearch => {
		const id = raw.id;
		if (typeof id === "string" && id.trim().length > 0) {
			return { id: id.trim() };
		}
		return {};
	},
	component: SettingsSubnetworkFaviconRoute,
});

export default settingsSubnetworkFaviconRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + heading + the live feature component.
// ---------------------------------------------------------------------------

export function SettingsSubnetworkFaviconRoute() {
	const { id } = settingsSubnetworkFaviconRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<SubnetworkFaviconPage initialSubnetworkId={id} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Settings</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Subnetwork favicon</li>
			</ol>
		</nav>
	);
}
